/**
 * Match API – all match mutations go through Cloud Functions (backend).
 * Passes actAsUserId when impersonating so backend can attribute actions correctly.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';
import { getActAsUserId } from '../impersonationStore';

const functions = getFunctions(app, 'us-central1');

function call(name, data = {}) {
  const fn = httpsCallable(functions, name);
  const actAs = getActAsUserId();
  const payload = actAs ? { ...data, actAsUserId: actAs } : data;
  return fn(payload);
}

/**
 * @param {string} matchId
 * @param {string} [actorName] - display name for audit log
 */
export async function matchStart(matchId, actorName = '') {
  const result = await call('matchStart', { matchId, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {string} [actorName]
 */
export async function matchAssignReferee(matchId, actorName = '') {
  const result = await call('matchAssignReferee', { matchId, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {object} scores - full scores payload (game1, game2, game3, services, etc.)
 * @param {string} [actorName]
 */
export async function matchUpdateScores(matchId, scores, actorName = '') {
  const result = await call('matchUpdateScores', { matchId, scores, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {object} scores - full scores with first server set
 * @param {string} [actorName]
 */
export async function matchSetFirstServer(matchId, scores, actorName = '') {
  const result = await call('matchSetFirstServer', { matchId, scores, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {object} payload - { scores, winnerId, winnerIds, loserIds, scoreSummary }
 * @param {string} [actorName]
 */
export async function matchComplete(matchId, payload, actorName = '') {
  const result = await call('matchComplete', { matchId, ...payload, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {object} [payload] - { requestedDate, requestedTime, reason }
 * @param {string} [actorName]
 */
export async function matchRescheduleRequest(matchId, payload = {}, actorName = '') {
  const result = await call('matchRescheduleRequest', { matchId, ...payload, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {string} [actorName]
 */
export async function matchRescheduleApprove(matchId, actorName = '') {
  const result = await call('matchRescheduleApprove', { matchId, actorName });
  return result.data;
}

/**
 * @param {string} matchId
 * @param {object} [payload] - { scheduledAt (UTC ms), venueBlockId } or legacy { scheduledDate, scheduledTime }
 * @param {string} [actorName]
 */
export async function matchRescheduleComplete(matchId, payload = {}, actorName = '') {
  const { scheduledAt, scheduledDate, scheduledTime, venueBlockId, clientUpdatedAt, forceOverwrite } = payload;
  const result = await call('matchRescheduleComplete', {
    matchId,
    scheduledAt: scheduledAt ?? (scheduledDate instanceof Date ? scheduledDate.getTime() : scheduledDate),
    scheduledDate: scheduledDate instanceof Date ? scheduledDate.getTime() : scheduledDate,
    scheduledTime,
    venueBlockId,
    actorName,
    clientUpdatedAt: clientUpdatedAt ?? undefined,
    forceOverwrite: forceOverwrite === true,
  });
  return result;
}
